import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Modal,
  Form,
  Table,
} from "react-bootstrap";
import Breadcrumb from "app/components/Breadcrumb";
import api from "app/services/api";

export default function LeavePolicy() {
  const [policies, setPolicies] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
  });

  const [leaveRows, setLeaveRows] = useState([
    { type: "Sick Leave", days: "", isPaid: true },
  ]);

  // View Policy Details Modal
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [viewPolicy, setViewPolicy] = useState(null);

  /* ================= FETCH DATA ================= */
  const fetchPolicies = async () => {
    try {
      const res = await api.get("/api/leave-policy/list");

      // FIX: safe handling (backend may return array or {data})
      setPolicies(res.data.data || res.data || []);
    } catch (err) {
      console.error("Fetch error:", err);
    }
  };

  useEffect(() => {
    fetchPolicies();
  }, []);

  /* ================= INPUT ================= */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleRowChange = (index, field, value) => {
    const updated = [...leaveRows];
    updated[index][field] = value;
    setLeaveRows(updated);
  };

  const addRow = () => {
    setLeaveRows([...leaveRows, { type: "", days: "", isPaid: true }]);
  };

  const removeRow = (index) => {
    setLeaveRows((prev) => prev.filter((_, i) => i !== index));
  };

  /* ================= SAVE ================= */
  const handleSave = async () => {
    try {
      // Validate required fields
      if (!formData.title.trim()) {
        alert("Please enter a policy title");
        return;
      }

      console.log("DEBUG - leaveRows before filter:", leaveRows);

      // Map leaveRows to components format for new backend
      // Check for type (non-empty string) and days (any truthy value including "0" as string)
      const validRows = leaveRows.filter(row => {
        const hasType = row.type?.trim?.();
        const hasDays = row.days !== "" && row.days !== null && row.days !== undefined;
        console.log("Row check:", { type: row.type, days: row.days, hasType, hasDays });
        return hasType && hasDays;
      });
      
      console.log("DEBUG - validRows after filter:", validRows);

      if (validRows.length === 0) {
        alert("Please add at least one leave type with days");
        return;
      }

      const components = validRows.map(row => ({
        leave_type_id: row.leave_type_id || null,
        leave_type: row.type.trim(),
        allocated_days: parseInt(row.days) || 0,
        is_paid: row.isPaid !== undefined ? row.isPaid : true,
        allocated_day: parseInt(row.days) || 0, // for backward compatibility
      }));

      console.log("DEBUG - components:", components);

      const payload = {
        title: formData.title.trim(),
        description: formData.description?.trim() || "",
        level: 1, // default level
        components: components,
        leaves: validRows, // keep for backward compatibility
      };

      console.log("DEBUG - final payload:", payload);

      if (editData) {
        await api.put(
          `/api/leave-policy/update/${editData.id}`,
          payload
        );
        alert("Leave policy updated successfully!");
      } else {
        await api.post("/api/leave-policy/create", payload);
        alert("Leave policy created successfully!");
      }

      await fetchPolicies();

      setShowModal(false);
      setEditData(null);
      setFormData({ title: "", description: "" });
      setLeaveRows([{ type: "Sick Leave", days: "", isPaid: true }]);
    } catch (err) {
      console.error("Save error:", err);
      const errorMsg = err.response?.data?.message || err.message || "Unknown error";
      alert(`Failed to save leave policy: ${errorMsg}`);
    }
  };

  /* ================= EDIT ================= */
  const handleEdit = (item) => {
    setEditData(item);

    setFormData({
      title: item.title,
      description: item.description,
    });

    // Map components to leaves format for editing
    const leaves = item.components?.map(c => ({
      type: c.leave_type || c.type || "",
      days: c.allocated_days ?? c.days ?? c.allocated_day ?? "",
      isPaid: c.is_paid ?? c.isPaid ?? true,
      leave_type_id: c.leave_type_id || c.id || null
    })) || item.leaves?.map(l => ({
      type: l.type || l.leave_type || "",
      days: l.days ?? l.allocated_days ?? "",
      isPaid: l.isPaid ?? l.is_paid ?? true,
      leave_type_id: l.leave_type_id || l.id || null
    })) || [{ type: "", days: "", isPaid: true }];
    setLeaveRows(leaves);
    setShowModal(true);
  };

  /* ================= VIEW DETAILS ================= */
  const handleViewDetails = (item) => {
    setViewPolicy(item);
    setShowDetailsModal(true);
  };

/* ================= DELETE ================= */
const handleDelete = async (id) => {
  const confirmDelete = window.confirm(
    "Are you sure you want to delete this leave policy?"
  );

  if (!confirmDelete) return;

  try {
    await api.delete(`/api/leave-policy/delete/${id}`);

    fetchPolicies();

    window.alert("Leave policy deleted successfully.");
  } catch (err) {
    console.error(err);

    window.alert("Failed to delete leave policy.");
  }
};

  return (
    <section>
      <Breadcrumb
        routeSegments={[
          { name: "Dashboard", path: "/" },
          { name: "Leave Management" },
        ]}
      />

      {/* HEADER */}
      <Row className="mb-3">
        <Col>
          <Card body>
            <Button
              style={{ backgroundColor: "#663399", border: "none" }}
              onClick={() => {
                setEditData(null);
                setFormData({ title: "", description: "" });
                setLeaveRows([{ type: "Sick Leave", days: "" }]);
                setShowModal(true);
              }}
            >
              Add Leave Policy
            </Button>
          </Card>
        </Col>
      </Row>

      {/* TABLE */}
      <Card body>
        <Card.Title>Leave Policies</Card.Title>

        <Table bordered>
          <thead style={{ backgroundColor: "#cfc7d9" }}>
            <tr>
              <th style={{ backgroundColor: "#cfc7d9" }}>No</th>
               <th style={{ backgroundColor: "#cfc7d9" }}>Title</th>
               <th style={{ backgroundColor: "#cfc7d9" }}>Description</th>
               <th style={{ backgroundColor: "#cfc7d9" }}>Leaves</th>
               <th style={{ backgroundColor: "#cfc7d9" }}>Action</th>
            </tr>
          </thead>

          <tbody>
            {policies.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center">
                  No Data
                </td>
              </tr>
            ) : (
              policies.map((item, index) => (
                <tr key={item.id || index}>
                  <td>{index + 1}</td>
                  <td>{item.title}</td>
                  <td>{item.description}</td>

                  <td>
                    {/* Support both old format (leaves) and new format (components) */}
                    {(item.components || item.leaves)?.map((l, i) => (
                      <div key={i} className="d-flex align-items-center gap-2 mb-1">
                        <span className="fw-medium">{l.leave_type || l.type}</span>
                        <span className="text-muted">({l.allocated_days || l.days} days)</span>
                        <span
                          className="badge"
                          style={{
                            fontSize: "10px",
                            padding: "2px 8px",
                            backgroundColor: (l.is_paid !== undefined ? l.is_paid : l.isPaid) ? "#28a745" : "#dc3545",
                            color: "white",
                            borderRadius: "4px",
                          }}
                        >
                          {(l.is_paid !== undefined ? l.is_paid : l.isPaid) ? "Paid" : "Unpaid"}
                        </span>
                      </div>
                    ))}
                    {(!(item.components || item.leaves) || (item.components || item.leaves).length === 0) && (
                      <span className="text-muted">No leaves defined</span>
                    )}
                  </td>

                 <td>
  <div className="d-flex gap-2">
    <Button
      style={{
        width: "32px",
        height: "32px",
        backgroundColor: "#20c997",
        border: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 0,
      }}
      onClick={() => handleViewDetails(item)}
    >
      <i className="i-Eye text-white"></i>
    </Button>

    <Button
      style={{
        width: "32px",
        height: "32px",
        backgroundColor: "#663399",
        border: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 0,
      }}
      onClick={() => handleEdit(item)}
    >
      <i className="i-Edit text-white"></i>
    </Button>

    <Button
      style={{
        width: "32px",
        height: "32px",
        backgroundColor: "#c4302b",
        border: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 0,
      }}
      onClick={() => handleDelete(item.id)}
    >
      <i className="i-Close-Window text-white"></i>
    </Button>
  </div>
</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Card>

      {/* MODAL */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editData ? "Edit Policy" : "Add Leave Policy"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              name="title"
              value={formData.title}
              onChange={handleChange}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              name="description"
              value={formData.description}
              onChange={handleChange}
            />
          </Form.Group>

          <h6>Leave Components</h6>

          <Table bordered size="sm">
            <thead>
              <tr>
                <th>Type</th>
                <th>Days</th>
                <th>Paid</th>
                <th>Add</th>
              </tr>
            </thead>

            <tbody>
              {leaveRows.map((row, index) => (
                <tr key={index}>
                  <td>
                    <Form.Control
                      value={row.type}
                      onChange={(e) => handleRowChange(index, "type", e.target.value)}
                    />
                  </td>

                  <td>
                    <Form.Control
                      type="number"
                      value={row.days}
                      onChange={(e) => handleRowChange(index, "days", e.target.value)}
                    />
                  </td>

                  <td style={{ verticalAlign: "middle" }}>
                    <Form.Check
                      type="switch"
                      id={`isPaid-switch-${index}`}
                      checked={row.isPaid === undefined ? true : row.isPaid}
                      onChange={(e) => handleRowChange(index, "isPaid", e.target.checked)}
                      label={row.isPaid === undefined ? "Paid" : row.isPaid ? "Paid" : "Unpaid"}
                    />
                  </td>

                  <td>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => removeRow(index)}
                      disabled={leaveRows.length === 1}
                      style={{ marginRight: "6px" }}
                    >
                      ✕
                    </Button>

                    {index === leaveRows.length - 1 && (
                      <Button
                        onClick={addRow}
                        style={{ backgroundColor: "#663399", border: "none" }}
                      >
                        +
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>

        <Modal.Footer>
          <Button
            style={{ backgroundColor: "#663399", border: "none" }}
            onClick={handleSave}
          >
            {editData ? "Update" : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* VIEW POLICY DETAILS MODAL */}
      <Modal
        show={showDetailsModal}
        onHide={() => setShowDetailsModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton style={{ backgroundColor: "#f8f9fa" }}>
          <Modal.Title style={{ color: "#663399", fontWeight: 600 }}>
            {viewPolicy?.title || "Policy Details"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewPolicy && (
            <>
              <div className="mb-4">
                <h6 className="text-muted mb-2">Description</h6>
                <p className="mb-0">{viewPolicy.description || "No description available"}</p>
              </div>

              <h6 className="text-muted mb-3">Leave Components</h6>
              <Table bordered size="sm">
                <thead style={{ backgroundColor: "#e9ecef" }}>
                  <tr>
                    <th style={{ width: "40%" }}>Leave Type</th>
                    <th style={{ width: "25%" }}>Days Allowed</th>
                    <th style={{ width: "35%" }}>Payment Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(viewPolicy.components || viewPolicy.leaves)?.length > 0 ? (
                    (viewPolicy.components || viewPolicy.leaves).map((l, i) => (
                      <tr key={i}>
                        <td className="fw-medium">{l.leave_type || l.type}</td>
                        <td>{l.allocated_days || l.days} days</td>
                        <td>
                          <span
                            className="badge"
                            style={{
                              fontSize: "11px",
                              padding: "4px 12px",
                              backgroundColor: (l.is_paid !== undefined ? l.is_paid : l.isPaid) ? "#28a745" : "#dc3545",
                              color: "white",
                            }}
                          >
                            {(l.is_paid !== undefined ? l.is_paid : l.isPaid) ? "Paid Leave" : "Unpaid Leave"}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="text-center text-muted py-3">
                        No leave components defined for this policy
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>

              {(viewPolicy.components || viewPolicy.leaves)?.length > 0 && (
                <div className="mt-3 p-3 bg-light rounded">
                  <div className="d-flex justify-content-between">
                    <span className="text-muted">Total Leave Types:</span>
                    <span className="fw-bold">{(viewPolicy.components || viewPolicy.leaves).length}</span>
                  </div>
                  <div className="d-flex justify-content-between mt-1">
                    <span className="text-muted">Total Days (Paid):</span>
                    <span className="fw-bold text-success">
                      {(viewPolicy.components || viewPolicy.leaves).filter(l => (l.is_paid !== undefined ? l.is_paid : l.isPaid)).reduce((sum, l) => sum + Number(l.allocated_days || l.days || 0), 0)} days
                    </span>
                  </div>
                  <div className="d-flex justify-content-between mt-1">
                    <span className="text-muted">Total Days (Unpaid):</span>
                    <span className="fw-bold text-danger">
                      {(viewPolicy.components || viewPolicy.leaves).filter(l => !(l.is_paid !== undefined ? l.is_paid : l.isPaid)).reduce((sum, l) => sum + Number(l.allocated_days || l.days || 0), 0)} days
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
          {viewPolicy && (
            <Button
              style={{ backgroundColor: "#663399", border: "none" }}
              onClick={() => {
                setShowDetailsModal(false);
                handleEdit(viewPolicy);
              }}
            >
              Edit Policy
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </section>
  );
}